import { parse as csvParse } from 'csv-parse/sync'
import { ValidationError, XMLValidator } from 'fast-xml-parser'
import { load as yamlLoad } from 'js-yaml'

export interface ValidationResult {
  isValid: boolean
  error?: string
  line?: number
  column?: number
}

interface ValidationStrategy {
  validate: (content: string) => ValidationResult
}

class XMLValidationStrategy implements ValidationStrategy {
  validate(content: string): ValidationResult {
    if (!content.trim()) {
      return { isValid: true }
    }

    try {
      const result = XMLValidator.validate(content, { allowBooleanAttributes: true })
      if (result === true) {
        return { isValid: true }
      } else {
        const { line, col, msg } = result.err
        return {
          isValid: false,
          error: `Invalid XML: ${msg}`,
          line,
          column: col
        }
      }
    } catch (error) {
      const err = error as ValidationError
      return {
        isValid: false,
        error: `Invalid XML: ${err.err}`,
        line: err.err.line,
        column: err.err.col
      }
    }
  }
}

class JSONValidationStrategy implements ValidationStrategy {
  validate(content: string): ValidationResult {
    if (!content.trim()) {
      return { isValid: true }
    }

    try {
      JSON.parse(content)
      return { isValid: true }
    } catch (error) {
      const err = error as SyntaxError
      const match = err.message.match(/at position (\d+)/)
      const position = match ? parseInt(match[1]) : 0
      const lines = content.slice(0, position).split('\n')
      return {
        isValid: false,
        error: `Invalid JSON: ${err.message}`,
        line: lines.length - 1,
        column: lines[lines.length - 1].length + 1
      }
    }
  }
}

class CSVValidationStrategy implements ValidationStrategy {
  validate(content: string): ValidationResult {
    if (!content.trim()) {
      return { isValid: true }
    }

    try {
      csvParse(content, {
        skip_empty_lines: true,
        columns: true,
        trim: true
      })
      return { isValid: true }
    } catch (error) {
      const err = error as Error
      const match = err.message.match(/\(line (\d+)\)/)
      const line = match ? parseInt(match[1]) : 1
      return {
        isValid: false,
        error: `Invalid CSV: ${err.message}`,
        line,
        column: 1
      }
    }
  }
}

class YAMLValidationStrategy implements ValidationStrategy {
  validate(content: string): ValidationResult {
    if (!content.trim()) {
      return { isValid: true }
    }

    try {
      yamlLoad(content)
      return { isValid: true }
    } catch (error) {
      const err = error as Error
      const match = err.message.match(/line (\d+), column (\d+):/)
      const line = match ? parseInt(match[1]) : 1
      const column = match ? parseInt(match[2]) : 1
      return {
        isValid: false,
        error: `Invalid YAML: ${err.message}`,
        line,
        column
      }
    }
  }
}

class EdiValidationStrategy implements ValidationStrategy {
  validate(content: string): ValidationResult {
    // For EDI files, we do a basic validation
    if (!content.trim()) {
      return { isValid: true }
    }

    return { isValid: true } // Basic implementation - assuming valid EDI
  }
}

class HL7ValidationStrategy implements ValidationStrategy {
  validate(content: string): ValidationResult {
    // For HL7 files, we do a basic validation
    if (!content.trim()) {
      return { isValid: true }
    }

    // Very basic validation: check if it looks like HL7 format
    // Typically HL7 messages start with MSH segment
    if (content.trim().startsWith('MSH|') || content.trim().startsWith('MSH^')) {
      return { isValid: true }
    }

    return {
      isValid: true, // For now, accept all content as valid to avoid blocking users
      error:
        'Warning: This may not be a valid HL7 message. HL7 messages typically start with MSH segment.'
    }
  }
}

export const validationStrategies: Record<string, ValidationStrategy> = {
  xml: new XMLValidationStrategy(),
  json: new JSONValidationStrategy(),
  csv: new CSVValidationStrategy(),
  yaml: new YAMLValidationStrategy(),
  edi: new EdiValidationStrategy(),
  hl7: new HL7ValidationStrategy()
}
